import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { Box, Button, Divider, Flex, Text } from '@awning/components';
import { TEstimatorSearchItem, TNoSearchBarResult } from '@/src/SearchBox/types';
import { ESTIMATOR_BASE_MODEL_PARAMS } from '@/src/estimator/store';
import { ReactComponent as CloseIcon } from '@awning/components/assets/icons/x.svg';
import { ReactComponent as MinusIcon } from '@awning/components/assets/icons/minus.svg';
import { ReactComponent as PlusIcon } from '@awning/components/assets/icons/plus.svg';
import {
  BaseStep,
  SearchBox,
  SearchBoxContext,
  SearchBoxItemsCollectionClass,
  SearchBoxStep,
  useAutoCompleteAndGeocoder,
} from '@/src/SearchBox';
import useNoResultsModal from '@/src/SearchBox/useNoResultsModal';

const itemToString = <T extends TEstimatorSearchItem>(item: T) => {
  if (item === null || item === undefined) return '';

  if ('isEmptyResult' in item) {
    return (item as TNoSearchBarResult).query;
  }

  return (item as google.maps.places.QueryAutocompletePrediction).description;
};

const baseState = {
  modelParams: ESTIMATOR_BASE_MODEL_PARAMS,
  step: 1,
  selectedItem: null as unknown as TEstimatorSearchItem,
  hasChangedGuests: false,
};

class TEstimatorSearchBoxCollection<
  T extends TEstimatorSearchItem[] = any
> extends SearchBoxItemsCollectionClass {
  constructor(public items: T) {
    super(items);
  }

  getCount() {
    return this.items.length;
  }

  getItemByIndex(index: number) {
    return this.items[index];
  }

  itemToString(item: any): string {
    return itemToString(item);
  }
}

export const SearchBar: React.FC = () => {
  const { query, push } = useRouter();
  const NoResultsModal = useNoResultsModal();
  const { autocomplete, geocoder } = useAutoCompleteAndGeocoder();

  const [items, setItems] = React.useState<TEstimatorSearchItem[]>([]);
  const collection = React.useMemo(() => new TEstimatorSearchBoxCollection(items), [items]);

  const value = itemToString(
    query?.address
      ? ({
          description: Array.isArray(query.address) ? query.address[0] : query.address,
        } as any)
      : null
  );

  const [state, dispatch] = React.useReducer(
    (state: typeof baseState, action: { type: string; payload: Partial<typeof baseState> }) => {
      switch (action.type) {
        case 'reset':
          return baseState;
        case 'update': {
          if (action.payload.hasChangedGuests) {
            return {
              ...state,
              ...action.payload,
            };
          } else if (!state.hasChangedGuests && action.payload.modelParams) {
            let maxGuests =
              (action.payload.modelParams.bedrooms ?? state.modelParams.bedrooms) * 2 + 2;
            maxGuests = maxGuests > 16 ? 16 : maxGuests;
            return {
              ...state,
              modelParams: {
                ...action.payload.modelParams,
                maxGuests: maxGuests,
              },
            };
          }
          return {
            ...state,
            ...action.payload,
          };
        }
      }
      return baseState;
    },
    baseState
  );

  const onChange = async (inputValue: string, init: boolean = false) => {
    if (!autocomplete) return;

    if (inputValue === '') {
      setItems([]);
      dispatch({ type: 'reset', payload: {} });
      return;
    }

    try {
      const response: google.maps.places.AutocompleteResponse =
        await autocomplete.getPlacePredictions({
          input: inputValue,
          types: ['address'],
          componentRestrictions: { country: 'us' },
        });

      if (!response || isEmpty(response.predictions)) {
        setItems([{ isEmptyResult: true, query: inputValue }]);
        dispatch({ type: 'update', payload: { step: 1 } });
        return;
      }

      setItems(response.predictions);
      const selectedItem = response.predictions?.[0];
      if (selectedItem && init) {
        dispatch({ type: 'update', payload: { step: 2, selectedItem } });
      } else {
        dispatch({ type: 'update', payload: { step: 1, selectedItem: undefined } });
      }
    } catch {
      setItems([]);
    }
  };

  const onStep2Click = (key: string, value: any) => {
    const _v = typeof value === 'number' ? Math.max(1, value) : value;

    if (key === 'maxGuests') {
      dispatch({
        type: 'update',
        payload: {
          modelParams: {
            ...state.modelParams,
            [key]: value > 16 ? value : Math.max(1, value), // minimum max guests is 1
          },
          hasChangedGuests: true,
        },
      });
    } else {
      dispatch({
        type: 'update',
        payload: {
          modelParams: {
            ...state.modelParams,
            [key]: _v,
          },
        },
      });
    }
  };

  const onSubmit = async (item: TEstimatorSearchItem) => {
    let selectedItem = item || state.selectedItem;
    if (!selectedItem) {
      dispatch({ type: 'update', payload: { step: 1, selectedItem } });
      return;
    }
    if (!geocoder || (selectedItem === null && !query.address)) return;

    // If the SelectedItem does not have Autocomplete results
    if ('isEmptyResult' in selectedItem) {
      NoResultsModal.show();
    } else {
      if (state.step === 1) {
        dispatch({ type: 'update', payload: { selectedItem, step: 2 } });
      } else if (state.step === 2 && selectedItem) {
        const address = selectedItem.description;

        // If a user is searching a new address, get the new lat/lng
        const geocode: google.maps.GeocoderResponse = await geocoder.geocode({
          address,
        });

        push({
          pathname: '/estimator',
          query: {
            address,
            lat: geocode.results[0].geometry.location.lat(),
            lng: geocode.results[0].geometry.location.lng(),
            modelParams: JSON.stringify(state.modelParams),
          },
        }).then(() => {
          // dispatch({ type: 'reset', payload: {} });
          // dispatch({ type: 'reset' });
          dispatch({
            type: 'update',
            payload: { step: 2, selectedItem },
          });
        });
      }
    }
  };

  React.useEffect(() => {
    onChange(value, true);
  }, [autocomplete, value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <SearchBox<TEstimatorSearchBoxCollection>
        onChange={onChange}
        onSelect={onSubmit}
        collection={collection}
        value={value}
        isMultiStep={true}
        step={state.step}
        placeholder="Type an address"
      >
        <SearchBoxStep step={1}>
          <BaseStep />
        </SearchBoxStep>
        <SearchBoxStep step={2}>
          <Step2 onClick={onStep2Click} state={state} />
        </SearchBoxStep>
      </SearchBox>
      <NoResultsModal.Modal isShow={NoResultsModal.isShow}>
        <Flex
          sx={{
            backgroundColor: 'white',
            borderRadius: 'lg',
            flexDirection: 'column',
            maxWidth: { base: '100%', sm: '850px' },
            padding: 4,
            margin: { base: 4, sm: 0 },
          }}
        >
          <Box
            as={CloseIcon}
            height="18px"
            width="18px"
            sx={{ alignSelf: 'flex-end', cursor: 'pointer' }}
            onClick={NoResultsModal.hide}
          />
          <Text sx={{ marginBottom: { base: 5, sm: 6 } }}>
            Please check the spelling, try clearing the search box, or try reformatting to match
            these examples:
          </Text>
          <Text sx={{ marginBottom: 6 }}>
            <strong>City:</strong> Los Angeles, CA
            <br />
            <strong>Zip:</strong> 75204
            <br />
          </Text>
          <Text sx={{ color: 'gray.500', text: 'sm' }}>
            Note, if a city/zip wasn't found, try checking the spelling or see if it has another
            name. Some cities and zips won't appear because they don't have enough data to provide
            reasonable insights and conclusions.
          </Text>
        </Flex>
      </NoResultsModal.Modal>
    </Box>
  );
};

const Step2: React.FC<{ state: typeof baseState; onClick: Function }> = ({ state, onClick }) => {
  const { highlightedItemIndex, onSelect } = React.useContext(SearchBoxContext);

  const isDisabledButton = ![state.modelParams.hasHotTub, state.modelParams.hasPool].every(
    m => typeof m === 'boolean'
  );

  return (
    <Flex
      sx={{
        background: 'white',
        flexDirection: 'column',
        minHeight: 'fit-content',
      }}
    >
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          paddingY: 4,
          text: 'sm',
          fontWeight: 'bold',
          borderBottom: '1px solid',
          borderColor: 'gray.200',
        }}
      >
        Enter home details
      </Flex>
      <Box sx={{ paddingY: 4, paddingX: 6 }}>
        <Flex
          sx={{
            paddingBottom: 4,
            justifyContent: 'space-between',
            text: 'sm',
            aliginItems: 'center',
          }}
        >
          <Box sx={{ lineHeight: 2.5 }}>Bedrooms</Box>
          <Flex sx={{ alignItems: 'center', gap: 4 }}>
            <Flex
              as="button"
              sx={{
                borderRadius: 'full',
                border: '1px solid',
                borderColor: 'gray.300',
                padding: 2,
              }}
              onClick={() => onClick('bedrooms', state.modelParams.bedrooms - 1)}
            >
              <MinusIcon width="12px" />
            </Flex>
            <Box>{state.modelParams.bedrooms}</Box>

            <Flex
              as="button"
              sx={{
                borderRadius: 'full',
                border: '1px solid',
                borderColor: 'gray.300',
                padding: 2,
              }}
              onClick={() => onClick('bedrooms', state.modelParams.bedrooms + 1)}
            >
              <PlusIcon width="12px" />
            </Flex>
          </Flex>
        </Flex>
        <Divider sx={{ backgroundColor: 'gray.200' }} />
        <Flex
          sx={{
            paddingY: 4,
            justifyContent: 'space-between',
            text: 'sm',
            aliginItems: 'center',
          }}
        >
          <Box sx={{ lineHeight: 2.5 }}>Bathrooms</Box>
          <Flex sx={{ alignItems: 'center', gap: 4 }}>
            <Flex
              as="button"
              sx={{
                borderRadius: 'full',
                border: '1px solid',
                borderColor: 'gray.300',
                padding: 2,
              }}
              onClick={() => onClick('bathrooms', state.modelParams.bathrooms - 1)}
            >
              <MinusIcon width="12px" />
            </Flex>
            <Box>{state.modelParams.bathrooms}</Box>

            <Flex
              as="button"
              sx={{
                borderRadius: 'full',
                border: '1px solid',
                borderColor: 'gray.300',
                padding: 2,
              }}
              onClick={() => onClick('bathrooms', state.modelParams.bathrooms + 1)}
            >
              <PlusIcon width="12px" />
            </Flex>
          </Flex>
        </Flex>
        <Divider sx={{ backgroundColor: 'gray.200' }} />
        <Flex
          sx={{
            paddingY: 4,
            justifyContent: 'space-between',
            text: 'sm',
            aliginItems: 'center',
          }}
        >
          <Box sx={{ lineHeight: 2.5 }}>Guests you can accomodate</Box>
          <Flex sx={{ alignItems: 'center', gap: 4 }}>
            <Flex
              as="button"
              sx={{
                borderRadius: 'full',
                border: '1px solid',
                borderColor: 'gray.300',
                padding: 2,
              }}
              onClick={() => onClick('maxGuests', state.modelParams.maxGuests - 1)}
            >
              <MinusIcon width="12px" />
            </Flex>
            <Box>{state.modelParams.maxGuests}</Box>

            <Flex
              as="button"
              sx={{
                borderRadius: 'full',
                border: '1px solid',
                borderColor: 'gray.300',
                padding: 2,
              }}
              onClick={() => onClick('maxGuests', state.modelParams.maxGuests + 1)}
            >
              <PlusIcon width="12px" />
            </Flex>
          </Flex>
        </Flex>
        <Divider sx={{ backgroundColor: 'gray.200' }} />
        <Flex
          sx={{
            paddingY: 4,
            justifyContent: 'space-between',
            text: 'sm',
            aliginItems: 'center',
          }}
        >
          <Box sx={{ lineHeight: 2.5 }}>Has Pool</Box>
          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            <Flex
              as="button"
              sx={{
                borderRadius: '100px',
                border: '1px solid',
                borderColor: 'gray.300',
                paddingY: 1.5,
                paddingX: 4,
                backgroundColor: state.modelParams.hasPool ? 'black' : 'white',
                color: state.modelParams.hasPool ? 'white' : 'black',
              }}
              onClick={() => onClick('hasPool', true)}
            >
              Yes
            </Flex>
            <Flex
              as="button"
              sx={{
                borderRadius: '100px',
                border: '1px solid',
                borderColor: 'gray.300',
                paddingY: 1.5,
                paddingX: 4,
                backgroundColor:
                  typeof state.modelParams.hasPool === 'boolean' && !state.modelParams.hasPool
                    ? 'black'
                    : 'white',
                color:
                  typeof state.modelParams.hasPool === 'boolean' && !state.modelParams.hasPool
                    ? 'white'
                    : 'black',
              }}
              onClick={() => onClick('hasPool', false)}
            >
              No
            </Flex>
          </Flex>
        </Flex>
        <Divider sx={{ backgroundColor: 'gray.200' }} />
        <Flex
          sx={{
            paddingTop: 4,
            justifyContent: 'space-between',
            text: 'sm',
            aliginItems: 'center',
          }}
        >
          <Box sx={{ lineHeight: 2.5 }}>Has Hot Tub</Box>
          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            <Flex
              as="button"
              sx={{
                borderRadius: '100px',
                border: '1px solid',
                borderColor: 'gray.300',
                paddingY: 1.5,
                paddingX: 4,
                backgroundColor: state.modelParams.hasHotTub ? 'black' : 'white',
                color: state.modelParams.hasHotTub ? 'white' : 'black',
              }}
              onClick={() => onClick('hasHotTub', true)}
            >
              Yes
            </Flex>
            <Flex
              as="button"
              sx={{
                borderRadius: '100px',
                border: '1px solid',
                borderColor: 'gray.300',
                paddingY: 1.5,
                paddingX: 4,
                backgroundColor:
                  typeof state.modelParams.hasHotTub === 'boolean' && !state.modelParams.hasHotTub
                    ? 'black'
                    : 'white',
                color:
                  typeof state.modelParams.hasHotTub === 'boolean' && !state.modelParams.hasHotTub
                    ? 'white'
                    : 'black',
              }}
              onClick={() => onClick('hasHotTub', false)}
            >
              No
            </Flex>
          </Flex>
        </Flex>
      </Box>
      <Divider sx={{ backgroundColor: 'gray.200' }} />
      <Box sx={{ padding: 6, width: '100%' }}>
        <Button
          isDisabled={isDisabledButton}
          onPress={() => {
            onSelect?.(highlightedItemIndex);
          }}
        >
          Estimate
        </Button>
      </Box>
    </Flex>
  );
};
